import { Button } from "@gigpro/ui/Button";
import { Chip } from "@gigpro/ui/Chip";
import { DropdownMenuButton, DropdownMenuItem } from "@gigpro/ui/DropdownMenu";
import { CheckCircle, W2 } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { ToggleGroup, ToggleGroupItem } from "@gigpro/ui/ToggleGroup";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAdminPro, useAdminProW2EmploymentStatus, useUpdateVerification } from "api/queries/admin";
import { isFuture } from "date-fns";
import { suspensionsQuery } from "domains/admin/containers/ProSuspensions/queries";
import { SupportOpsCapability, useHasCapability } from "layouts/SuperAdminPanel/capabilities";
import { useFeatureFlag } from "providers/LaunchDarkly";
import React from "react";
import { ModalSendAiInterviewLink } from "./AiInterviewInvite/ModalSendAiInterviewLink";
import { BackPayProDialog } from "./BackPayProDialog";
import { ModalBackgroundCheckLink } from "./ModalBackgroundCheckLink";
import {
  proDetailTabs,
  useHasW2Onboarding,
  useIsProW2,
  useLatestInterview,
  usePageTitle,
  useToggleW2Onboarding,
} from "./utils";
import { W2EmploymentStatusChip } from "./W2Employment/W2EmploymentStatusChip";
import { W2EmploymentStatusDialog } from "./W2Employment/W2EmploymentStatusDialog";

type CommonProps = Readonly<{ proId: number }>;

interface TitleBarProps extends CommonProps, React.ComponentProps<"div"> {}
export function TitleBar({ proId, className, ...props }: TitleBarProps) {
  const { title, currentTo } = usePageTitle(proId);

  const showW2Badge = useIsProW2(proId);
  const sickTimeEnabled = useFeatureFlag("pro-sick-time");
  const showW2OnlyTabs = showW2Badge && sickTimeEnabled;
  const hasW2Onboarding = useHasW2Onboarding(proId);
  const toggleW2Onboarding = useToggleW2Onboarding(proId);

  const [isBackgroundCheckLinkModalOpen, setIsBackgroundCheckLinkModalOpen] = React.useState(false);
  const [isAiInterviewLinkModalOpen, setIsAiInterviewLinkModalOpen] = React.useState(false);
  const [isBackPayProModalOpen, setIsBackPayProModalOpen] = React.useState(false);
  const hasBackPayProCapability = useHasCapability(SupportOpsCapability.backPayPro);
  const showBackPayPro = hasBackPayProCapability;
  const { data: pro } = useAdminPro({ id: proId });
  const proPhone = pro?.phone_number ?? null;
  const showAiInterviewInvite = !!proPhone;
  const w2TerminationEnabled = useFeatureFlag("w2-termination");
  const { data: w2EmploymentStatus } = useAdminProW2EmploymentStatus(proId);
  const w2EmploymentAction = (() => {
    if (!w2TerminationEnabled) return null;
    if (w2EmploymentStatus === "onboarded") return "terminate";
    if (w2EmploymentStatus === "terminated") return "restore";
    return null;
  })();
  const [isW2EmploymentDialogOpen, setIsW2EmploymentDialogOpen] = React.useState(false);

  return (
    <div className={cn("flex items-center gap-2 bg-black py-2 pr-1 pl-3 text-white", className)} {...props}>
      {showW2Badge && <W2 color="currentColor" className="size-6 text-error" />}
      <h1 className="text-large font-semibold">{title}</h1>
      <SuspendedState proId={proId} />
      <W2EmploymentStatusChip proId={proId} />
      <VerificationToggle proId={proId} />
      <DropdownMenuButton side="bottom" className="px-2 text-white hover:bg-gray/50">
        {proDetailTabs
          .filter((tab) => showW2OnlyTabs || !("w2Only" in tab))
          .map((tab) => (
            <DropdownMenuItem key={tab.label} disabled={tab.to === currentTo} asChild>
              <Link to={tab.to} params={{ id: String(proId) }}>
                {tab.label}
              </Link>
            </DropdownMenuItem>
          ))}
        {!showW2Badge && (
          <DropdownMenuItem asChild>
            <Button className="w-full justify-start" onClick={() => toggleW2Onboarding.mutate()}>
              {hasW2Onboarding ? "Stop" : "Start"} W2 Onboarding
            </Button>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Button className="w-full justify-start" onClick={() => setIsBackgroundCheckLinkModalOpen(true)}>
            Send Background Check Link
          </Button>
        </DropdownMenuItem>
        {showAiInterviewInvite && (
          <DropdownMenuItem asChild>
            <Button className="w-full justify-start" onClick={() => setIsAiInterviewLinkModalOpen(true)}>
              Send Interview Link
            </Button>
          </DropdownMenuItem>
        )}
        {w2EmploymentAction && (
          <DropdownMenuItem asChild>
            <Button className="w-full justify-start" onClick={() => setIsW2EmploymentDialogOpen(true)}>
              {w2EmploymentAction === "terminate" ? "Mark W-2 terminated" : "Restore W-2 employment"}
            </Button>
          </DropdownMenuItem>
        )}
        {showBackPayPro && (
          <DropdownMenuItem asChild>
            <Button className="w-full justify-start" onClick={() => setIsBackPayProModalOpen(true)}>
              Back Pay Pro
            </Button>
          </DropdownMenuItem>
        )}
      </DropdownMenuButton>
      <ModalBackgroundCheckLink
        open={isBackgroundCheckLinkModalOpen}
        onClose={() => setIsBackgroundCheckLinkModalOpen(false)}
        proId={proId}
      />
      {showAiInterviewInvite && (
        <ModalSendAiInterviewLink
          open={isAiInterviewLinkModalOpen}
          onClose={() => setIsAiInterviewLinkModalOpen(false)}
          proId={proId}
          phone={proPhone}
        />
      )}
      {w2EmploymentAction && (
        <W2EmploymentStatusDialog
          open={isW2EmploymentDialogOpen}
          onClose={() => setIsW2EmploymentDialogOpen(false)}
          proId={proId}
          action={w2EmploymentAction}
        />
      )}
      {showBackPayPro && (
        <BackPayProDialog open={isBackPayProModalOpen} onOpenChange={setIsBackPayProModalOpen} proId={proId} />
      )}
    </div>
  );
}

function SuspendedState({ proId }: CommonProps) {
  const { data: suspensions } = useQuery({ ...suspensionsQuery({ proId }), staleTime: Number.POSITIVE_INFINITY });
  const isSuspended = (() => {
    const results = suspensions?.results;
    if (!results || results.length === 0) return false;
    return results.some((s) => (s.end_ts ? isFuture(s.end_ts) : true));
  })();

  return isSuspended ? <Chip className="bg-error text-large">Suspended</Chip> : null;
}

function VerificationToggle({ proId }: CommonProps) {
  const activeInterview = useLatestInterview(proId);
  const hasApprovedInterview = activeInterview.data?.status === "APPROVED";
  const pro = useAdminPro({ id: proId }, { enabled: !hasApprovedInterview, refetchOnWindowFocus: false });

  const updateVerification = useUpdateVerification();

  const isLoading = hasApprovedInterview
    ? false
    : updateVerification.isPending || pro.isFetching || activeInterview.isLoading;
  return (
    <ToggleGroup
      className="-my-0.5 ml-auto shrink-0 p-0"
      type="single"
      disabled={hasApprovedInterview || isLoading}
      value={hasApprovedInterview ? "EARNED" : (pro.data?.verification_status ?? undefined)}
      onValueChange={(newStatus) => {
        if (newStatus == null || !pro) return;
        updateVerification.mutate({ id: pro.data?.verification_id, pro_id: proId, verified: newStatus === "EARNED" });
      }}
    >
      <ToggleGroupItem
        className={cn(
          "flex items-center gap-1 p-2",
          hasApprovedInterview && "disabled:text-white data-[state=on]:disabled:bg-brand",
        )}
        value="EARNED"
      >
        {isLoading ? <div className="loading-spinner size-4" /> : <CheckCircle className="size-4" />}{" "}
        {hasApprovedInterview ? "Approved" : "Verified"}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
